import { useEffect, useState } from 'react'
import { Geolocation as CapacitorGeolocation } from '@capacitor/geolocation'

import { Coordinates } from '../api/defs/Coordinates.type'

const CACHE_COORDS_KEY = 'coords'
const CACHE_COORDS_TIMESTAMP_KEY = 'coordsTimestamp'

const GEOLOCATION_TIMEOUT = 10000 // 10 seconds
const GEOLOCATION_FALLBACK_TIMEOUT = 5000 // 5 seconds
export const GEOLOCATION_CACHE_TTL = 2.5 * 60 * 1000 // 2.5 minutes
const IOS_NATIVE_GEO_TIMEOUT =
    GEOLOCATION_TIMEOUT + GEOLOCATION_FALLBACK_TIMEOUT

let geolocationRequestInFlight: {
    allowDeviceCache: boolean
    promise: Promise<Coordinates>
} | null = null

export const GEO_ALLOWED_KEY = 'geoAllowed'
const GEO_PERMISSION_UPDATED_EVENT = 'geo-permission-updated'

const emitGeoPermissionUpdated = (
    status: 'granted' | 'denied' | 'prompt' | 'unsupported'
) => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(
        new CustomEvent(GEO_PERMISSION_UPDATED_EVENT, {
            detail: { status }
        })
    )
}

export const setGeoAllowed = () => {
    try {
        sessionStorage.setItem(GEO_ALLOWED_KEY, '1')
    } catch {}
}

export const getGeoAllowed = () => {
    try {
        return sessionStorage.getItem(GEO_ALLOWED_KEY) === '1'
    } catch {
        return false
    }
}

export const saveCoords = (coords: Coordinates) => {
    try {
        const currentTime = Date.now()
        const oldCoords = localStorage.getItem(CACHE_COORDS_KEY)

        // Only update if coordinates have changed
        if (oldCoords) {
            const parsedOldCoords = JSON.parse(oldCoords)
            if (
                Math.abs(parsedOldCoords.lat - coords.lat) < 0.00001 &&
                Math.abs(parsedOldCoords.lng - coords.lng) < 0.00001
            ) {
                // Keep cache alive even if position did not change,
                // so distance checks can reuse recent coordinates.
                localStorage.setItem(
                    CACHE_COORDS_TIMESTAMP_KEY,
                    currentTime.toString()
                )
                console.log('Coordinates unchanged, timestamp refreshed')
                return
            }
        }

        console.log('Saving coordinates:', coords)
        localStorage.setItem(CACHE_COORDS_KEY, JSON.stringify(coords))
        localStorage.setItem(CACHE_COORDS_TIMESTAMP_KEY, currentTime.toString())
    } catch (e) {
        console.error('Error while saving coords:', e)
    }
}

export const getCachedCoords = (): Coordinates | null => {
    try {
        const coords = localStorage.getItem(CACHE_COORDS_KEY)
        const timestamp = localStorage.getItem(CACHE_COORDS_TIMESTAMP_KEY)
        const currentTime = Date.now()

        if (!coords) return null

        // 2.5 minutes cache
        if (
            timestamp &&
            currentTime - parseInt(timestamp) > GEOLOCATION_CACHE_TTL
        ) {
            console.log('Cached coordinates expired')
            return null
        }

        return JSON.parse(coords)
    } catch (e) {
        console.error('Error while reading cached coords: ', e)
        return null
    }
}

export const invalidateCachedCoords = () => {
    try {
        localStorage.removeItem(CACHE_COORDS_KEY)
        localStorage.removeItem(CACHE_COORDS_TIMESTAMP_KEY)
        console.log('Invalidated cached coordinates')
    } catch (e) {
        console.error('Error while invalidating cached coords:', e)
    }
}

const getCurrentPosition = (options: any) =>
    new Promise<any>((resolve, reject) => {
        let settled = false
        let timeoutId: number | null = null
        const geolocationTimeout =
            typeof options?.timeout === 'number'
                ? options.timeout
                : GEOLOCATION_TIMEOUT
        const timeoutGuardMs = geolocationTimeout + 2000

        const resolveOnce = (position: any) => {
            if (settled) return
            settled = true
            if (timeoutId !== null) window.clearTimeout(timeoutId)
            resolve(position)
        }

        const rejectOnce = (error: any) => {
            if (settled) return
            settled = true
            if (timeoutId !== null) window.clearTimeout(timeoutId)
            reject(error)
        }

        timeoutId = window.setTimeout(() => {
            rejectOnce({
                code: 3,
                TIMEOUT: 3,
                PERMISSION_DENIED: 1,
                POSITION_UNAVAILABLE: 2,
                message: 'Geolocation request timed out'
            })
        }, timeoutGuardMs)

        try {
            navigator.geolocation.getCurrentPosition(
                resolveOnce,
                rejectOnce,
                options
            )
        } catch (error) {
            rejectOnce(error)
        }
    })

const mapGeolocationError = (error: any): Error => {
    let errorCode = 'geo/unknown-error'
    switch (error.code) {
        case error.PERMISSION_DENIED:
            errorCode = 'geo/permission-denied'
            break
        case error.POSITION_UNAVAILABLE:
            errorCode = 'geo/position-unavailable'
            break
        case error.TIMEOUT:
            errorCode = 'geo/timeout'
            break
    }

    return new Error(`${errorCode}: ${error.message}`)
}

const normalizeToError = (
    error: unknown,
    fallbackCode = 'geo/unknown-error'
): Error => {
    if (error instanceof Error) return error
    if (typeof error === 'string') return new Error(error)

    const possibleError = error as any
    if (typeof possibleError?.message === 'string') {
        return new Error(
            possibleError?.code
                ? `${possibleError.code}: ${possibleError.message}`
                : possibleError.message
        )
    }

    return new Error(fallbackCode)
}

const resolveMaximumAge = (allowDeviceCache: boolean) =>
    allowDeviceCache ? GEOLOCATION_CACHE_TTL : 0

const maybeGetCachedFallback = (error: unknown): Coordinates | null => {
    const geolocationError = error as any
    if (
        typeof geolocationError?.code === 'number' &&
        geolocationError.code === geolocationError.PERMISSION_DENIED
    ) {
        return null
    }

    return getCachedCoords()
}

const resolveBrowserGeolocation = async (
    allowDeviceCache = true
): Promise<Coordinates> => {
    if (!navigator.geolocation) {
        const cached = getCachedCoords()
        if (cached) return cached

        throw new Error('geo/not-supported')
    }

    try {
        const primaryPosition = await getCurrentPosition({
            enableHighAccuracy: true,
            timeout: GEOLOCATION_TIMEOUT,
            maximumAge: resolveMaximumAge(allowDeviceCache)
        })
        const coords = {
            lat: primaryPosition.coords.latitude,
            lng: primaryPosition.coords.longitude
        }
        saveCoords(coords)
        return coords
    } catch (error) {
        const geolocationError = error as any
        if (
            typeof geolocationError?.code === 'number' &&
            geolocationError.code === geolocationError.TIMEOUT
        ) {
            try {
                const fallbackPosition = await getCurrentPosition({
                    enableHighAccuracy: false,
                    timeout: GEOLOCATION_FALLBACK_TIMEOUT,
                    maximumAge: resolveMaximumAge(allowDeviceCache)
                })
                const coords = {
                    lat: fallbackPosition.coords.latitude,
                    lng: fallbackPosition.coords.longitude
                }
                saveCoords(coords)
                return coords
            } catch (fallbackError) {
                const cached = maybeGetCachedFallback(fallbackError)
                if (cached) {
                    console.log('Using cached coordinates as fallback:', cached)
                    return cached
                }

                const fallbackGeolocationError = fallbackError as any
                if (typeof fallbackGeolocationError?.code === 'number') {
                    throw mapGeolocationError(fallbackGeolocationError)
                }
                throw normalizeToError(fallbackError, 'geo/timeout')
            }
        }

        const cached = maybeGetCachedFallback(error)
        if (cached) {
            console.log('Using cached coordinates as fallback:', cached)
            return cached
        }

        if (typeof geolocationError?.code === 'number') {
            throw mapGeolocationError(geolocationError)
        }
        throw normalizeToError(error)
    }
}

const resolveFreshGeolocation = async (
    allowDeviceCache = true
): Promise<Coordinates> => {
    if (isIosNativeApp()) {
        try {
            const coords = await getGeolocationInIosNativeApp(allowDeviceCache)
            saveCoords(coords)
            return coords
        } catch (error) {
            throw normalizeToError(error)
        }
    }

    if (isAndroidNativeApp()) {
        try {
            const coords =
                await getGeolocationInAndroidNativeApp(allowDeviceCache)
            saveCoords(coords)
            return coords
        } catch (error) {
            throw normalizeToError(error)
        }
    }

    return resolveBrowserGeolocation(allowDeviceCache)
}

const getFreshGeolocationSingleFlight = (
    allowDeviceCache: boolean
): Promise<Coordinates> => {
    if (
        geolocationRequestInFlight &&
        geolocationRequestInFlight.allowDeviceCache === allowDeviceCache
    ) {
        return geolocationRequestInFlight.promise
    }

    const requestPromise = resolveFreshGeolocation(allowDeviceCache).finally(
        () => {
            if (geolocationRequestInFlight?.promise === requestPromise) {
                geolocationRequestInFlight = null
            }
        }
    )

    geolocationRequestInFlight = {
        allowDeviceCache,
        promise: requestPromise
    }

    return requestPromise
}

export const getGeolocation = (cache = false): Promise<Coordinates> => {
    if (cache) {
        const cachedCoords = getCachedCoords()
        if (cachedCoords) {
            console.log('Using cached coordinates:', cachedCoords)
            return Promise.resolve(cachedCoords)
        }
    }

    return getFreshGeolocationSingleFlight(cache)
}

export const prewarmGeolocation = () => {
    getGeolocation(true).catch(() => undefined)
}

const listenToGeolocationChanges = (
    // eslint-disable-next-line no-undef -- PermissionState is defined in lib.dom.d.ts
    onchange: (status: PermissionState) => void
): { stopListening: () => void } => {
    if (!supportsPermissions()) throw new Error('Permissions API not supported')

    const notifyOnChange = (status: PermissionStatus) => {
        // on certain browsers (e.g. Safari) permission is in 'prompt' or 'denied' state
        // when actually is granted, so we try to get access status by getting a location instead
        // if no error is thrown, we assume that permission is granted

        // ALWAYS verify with actual geolocation request on Safari/iOS
        // because Permissions API doesn't reflect system-level Location Services state
        if (status.state !== 'granted' || isMobileWeb()) {
            getGeolocationAccessStatusByGeolocationRequest()
                .then(s => onchange(s === 'granted' ? 'granted' : 'denied'))
                .catch(() => onchange('denied'))
        } else {
            onchange(status.state)
        }
    }

    const permissionStatus = navigator.permissions.query({
        name: 'geolocation'
    })
    permissionStatus
        .then(status => {
            status.onchange = () => notifyOnChange(status)
            // trigger on init
            notifyOnChange(status)
        })
        .catch(() => onchange('denied'))
    return {
        stopListening: () => {
            permissionStatus.then(status => {
                status.onchange = null
            })
        }
    }
}

// We need to check for permission without using Permissions API because
// it is not supported on older versions of iOS
export const getGeolocationAccessStatusByGeolocationRequest = async () => {
    return getGeolocation()
        .then(() => 'granted')
        .catch(() => {
            return 'denied'
            // eslint-disable-next-line no-undef -- PermissionState is defined in lib.dom.d.ts
        }) as Promise<PermissionState>
}

const normalizeCapacitorPermission = (
    permission: string | undefined
): 'granted' | 'denied' | 'prompt' => {
    if (permission === 'granted') return 'granted'
    if (permission === 'denied') return 'denied'
    return 'prompt'
}

const getCapacitorPermissionStatus = async (): Promise<
    'granted' | 'denied' | 'prompt'
> => {
    const status = await CapacitorGeolocation.checkPermissions()
    return normalizeCapacitorPermission(status?.location)
}

const mapCapacitorGeoError = (error: unknown): Error => {
    const message = String(
        (error as any)?.message || 'Unknown geolocation error'
    )
    const lower = message.toLowerCase()

    if (/(denied|not authorized|permission)/.test(lower)) {
        return new Error(`geo/permission-denied: ${message}`)
    }
    if (/timeout/.test(lower)) {
        return new Error(`geo/timeout: ${message}`)
    }
    if (/(position|location|unavailable|disabled)/.test(lower)) {
        return new Error(`geo/position-unavailable: ${message}`)
    }

    return normalizeToError(error, 'geo/unknown-error')
}

const ensureCapacitorGeoPermission = async (): Promise<void> => {
    const current = await CapacitorGeolocation.checkPermissions()
    const currentState = normalizeCapacitorPermission(current?.location)
    if (currentState === 'granted') return
    if (currentState === 'denied') {
        throw new Error('geo/permission-denied: iOS location permission denied')
    }

    const requested = await CapacitorGeolocation.requestPermissions()
    if (normalizeCapacitorPermission(requested?.location) !== 'granted') {
        throw new Error('geo/permission-denied: iOS location permission denied')
    }
}

const getGeolocationInIosNativeApp = async (
    allowDeviceCache = true
): Promise<Coordinates> => {
    try {
        await ensureCapacitorGeoPermission()
        const position = await CapacitorGeolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: IOS_NATIVE_GEO_TIMEOUT,
            maximumAge: resolveMaximumAge(allowDeviceCache)
        })

        const latitude = position?.coords?.latitude
        const longitude = position?.coords?.longitude
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            throw new Error('geo/position-unavailable')
        }

        return {
            lat: latitude,
            lng: longitude
        }
    } catch (error) {
        throw mapCapacitorGeoError(error)
    }
}

const getGeolocationInAndroidNativeApp = (
    allowDeviceCache = true
): Promise<Coordinates> => {
    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            position => {
                console.log('Android geolocation (network) success:', position)
                resolve({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                })
            },
            error => {
                console.error('Android geolocation error:', error)
                reject(new Error(`geo/position-unavailable: ${error.message}`))
            },
            {
                enableHighAccuracy: true, // Use network location
                timeout: 30000,
                maximumAge: allowDeviceCache ? 60000 : 0
            }
        )
    })
}
// This is a bit of a mess, so we do not make components unreadable
export const getGeolocationStatus = (
    // eslint-disable-next-line no-undef -- PermissionState is defined in lib.dom.d.ts
    onchange: (status: PermissionState) => void
): { stopListening: () => void } => {
    if (!supportsGeolocation()) throw new Error('Geolocation API not supported')

    // for iOS native app we need to use a custom method
    if (isIosNativeApp()) {
        getGeolocationInIosNativeApp()
            .then(() => onchange('granted'))
            .catch(() => onchange('denied'))
        return {
            stopListening: () => undefined
        }
    }

    // Here
    // for older versions of browsers we return the status based on geolocation request result
    if (supportsPermissions()) {
        return listenToGeolocationChanges(onchange)
    }

    getGeolocationAccessStatusByGeolocationRequest()
        .then(s => onchange(s))
        .catch(() => onchange('denied'))
    return {
        stopListening: () => undefined
    }
}

const getCapacitorPlatform = (): string | null => {
    if (typeof window === 'undefined') return null

    const win = window as any
    const cap = win?.Capacitor

    if (cap) {
        try {
            if (typeof cap.getPlatform === 'function') {
                const platform = cap.getPlatform()
                if (typeof platform === 'string') return platform
            }
        } catch {}

        if (typeof cap.platform === 'string') return cap.platform

        if (
            typeof cap.isNativePlatform === 'function' &&
            cap.isNativePlatform()
        ) {
            if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return 'ios'
            if (/Android/i.test(navigator.userAgent)) return 'android'
        }
    }

    // Fallbacks for native WebViews when window.Capacitor is not exposed yet.
    if (win?.androidBridge) return 'android'
    if (win?.webkit?.messageHandlers?.bridge) return 'ios'
    if (
        /; wv\)/i.test(navigator.userAgent) &&
        /Android/i.test(navigator.userAgent)
    ) {
        return 'android'
    }

    return null
}

// This is custom behavior for our iOS native app, we append userAgent with a custom suffix so we can detect it
export const isIosNativeApp = (): boolean => {
    const customSuffix = 'runningIniOSNative'
    const userAgent = window.navigator.userAgent
    if (userAgent.includes(customSuffix)) return true

    return getCapacitorPlatform() === 'ios'
}

export const isAndroidNativeApp = (): boolean => {
    const customSuffix = 'Android WebView'
    const userAgent = window.navigator.userAgent
    if (userAgent.includes(customSuffix)) return true

    return getCapacitorPlatform() === 'android'
}

export const isMobileWeb = () =>
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    window.matchMedia?.('(pointer: coarse)').matches ||
    new URLSearchParams(location.search).get('forceGeo') === '1'

export const isMobile = () =>
    isIosNativeApp() || isAndroidNativeApp() || isMobileWeb()

export const shouldOfferGeoOnThisPage = (
    section: 'warehouses' | 'myBoxes' | 'entries' | 'other',
    permission: GeoPermission
) => {
    if (!isMobile()) return false
    if (permission === 'granted') return false
    return (
        section === 'warehouses' ||
        section === 'myBoxes' ||
        section === 'entries'
    )
}
// this is a simple haversine distance calculation
export const haversineDistance = (
    coords1: Coordinates,
    coords2: Coordinates
) => {
    const toRad = (x: number): number => (x * Math.PI) / 180

    const R = 6378000 // Earth radius in meters
    const dLat = toRad(coords2.lat - coords1.lat)
    const dLng = toRad(coords2.lng - coords1.lng)
    const lat1 = toRad(coords1.lat)
    const lat2 = toRad(coords2.lat)

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLng / 2) *
            Math.sin(dLng / 2) *
            Math.cos(lat1) *
            Math.cos(lat2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
}

export const useGeolocationPermission = () => {
    const [permissionStatus, setPermissionStatus] = useState<
        'granted' | 'denied' | 'prompt' | 'unsupported'
    >('prompt')

    useEffect(() => {
        if (!supportsGeolocation()) {
            setPermissionStatus('unsupported')
            return
        }

        // In native WebViews the browser Permissions API can be unreliable,
        // so we read permission status from Capacitor geolocation plugin.
        if (isAndroidNativeApp() || isIosNativeApp()) {
            let mounted = true

            const refreshNativePermission = () => {
                getCapacitorPermissionStatus()
                    .then(status => {
                        if (mounted) setPermissionStatus(status)
                    })
                    .catch(() => {
                        if (!mounted) return
                        getGeolocation()
                            .then(() => setPermissionStatus('granted'))
                            .catch(() => setPermissionStatus('denied'))
                    })
            }

            const handlePermissionUpdate = (event: Event) => {
                const status = (event as CustomEvent<{ status?: string }>)
                    ?.detail?.status
                if (!status || !mounted) return
                setPermissionStatus(
                    status === 'granted' ||
                        status === 'denied' ||
                        status === 'prompt' ||
                        status === 'unsupported'
                        ? status
                        : 'prompt'
                )
            }

            refreshNativePermission()
            window.addEventListener(
                GEO_PERMISSION_UPDATED_EVENT,
                handlePermissionUpdate
            )

            return () => {
                mounted = false
                window.removeEventListener(
                    GEO_PERMISSION_UPDATED_EVENT,
                    handlePermissionUpdate
                )
            }
        }

        // Use Permissions API for other browsers
        const { stopListening } = getGeolocationStatus(setPermissionStatus)
        return () => stopListening?.()
    }, [])

    return permissionStatus
}

export const requestGeoPermission = async (): Promise<
    'granted' | 'denied' | 'unsupported'
> => {
    if (isAndroidNativeApp() || isIosNativeApp()) {
        try {
            const status = await CapacitorGeolocation.requestPermissions()
            const permission = normalizeCapacitorPermission(status?.location)
            emitGeoPermissionUpdated(permission)
            return permission === 'granted' ? 'granted' : 'denied'
        } catch {
            emitGeoPermissionUpdated('denied')
            return 'denied'
        }
    }

    if (!('geolocation' in navigator)) {
        emitGeoPermissionUpdated('unsupported')
        return 'unsupported'
    }
    try {
        await new Promise<any>((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej, {
                enableHighAccuracy: true,
                timeout: 12000
            })
        )
        emitGeoPermissionUpdated('granted')
        return 'granted'
    } catch {
        emitGeoPermissionUpdated('denied')
        return 'denied'
    }
}

export type GeoPermission = 'granted' | 'prompt' | 'denied' | 'unsupported'

interface GeoState {
    permission: GeoPermission
    position: any | null
    error: any | null
}

export function useGeolocation(): GeoState {
    const [state, set] = useState<GeoState>({
        permission: 'unsupported',
        position: null,
        error: null
    })

    useEffect(() => {
        let watchId: number | null = null
        let status: PermissionStatus | null = null

        const startWatch = () => {
            if (watchId != null) return
            watchId = navigator.geolocation.watchPosition(
                pos =>
                    set(s => ({
                        ...s,
                        permission: 'granted',
                        position: pos,
                        error: null
                    })),
                err =>
                    set(s => ({
                        ...s,
                        permission:
                            err.code === err.PERMISSION_DENIED
                                ? 'denied'
                                : s.permission,
                        error: err
                    })),
                { enableHighAccuracy: true }
            )
        }

        const stopWatch = () => {
            if (watchId != null) {
                navigator.geolocation.clearWatch(watchId)
                watchId = null
            }
        }

        if ('permissions' in navigator && navigator.permissions.query) {
            navigator.permissions
                .query({ name: 'geolocation' as any })
                .then(pStatus => {
                    status = pStatus
                    set(s => ({
                        ...s,
                        permission: pStatus.state as GeoPermission
                    }))
                    if (pStatus.state === 'granted') startWatch()
                    pStatus.onchange = () => {
                        set(s => ({
                            ...s,
                            permission: pStatus.state as GeoPermission
                        }))
                        if (pStatus.state === 'granted') startWatch()
                        else stopWatch()
                    }
                })
                .catch(() => {
                    set(s => ({ ...s, permission: 'unsupported' }))
                })
        }

        return () => {
            stopWatch()
            if (status) status.onchange = null
        }
    }, [])

    return state
}

export const supportsGeolocation = (): boolean => !!navigator.geolocation

export const supportsPermissions = () => !!navigator.permissions
